import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestoreIcon from '@mui/icons-material/Restore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTestConfigurations, useUserPrompts, useUserPrompt, useUserPromptVersions, useUserPromptBranches, useSetCurrentVersion } from '../../hooks/prompts';
import promptsService from '../../api/promptsService';
import authService from '../../api/authService';
import type { UserPrompt, CreateUserPromptDto } from '../../types/types';
import { t } from '../../i18n/t';

const statusLabel = (s: string) => {
    const map: Record<string, string> = {
        draft: t('prompts.status.draft'),
        active: t('prompts.status.active'),
        archived: t('prompts.status.archived'),
    };
    return map[s] ?? s;
};

interface UserPromptsManagerProps {
    userId: number;
    onClose?: () => void;
    table?: { getSelectedRowModel: () => { rows: { original: { id: number } }[] } };
}

export const UserPromptsManager: React.FC<UserPromptsManagerProps> = ({ userId, onClose, table }) => {
    const [filterConfigId, setFilterConfigId] = useState<number | ''>('');
    const [filterStatus, setFilterStatus] = useState<string>('');
    const [createOpen, setCreateOpen] = useState(false);
    const [editingPrompt, setEditingPrompt] = useState<UserPrompt | null>(null);
    const [versionsPromptId, setVersionsPromptId] = useState<number | null>(null);
    const [recommendations, setRecommendations] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);

    const { data: testConfigs = [], isLoading: loadingConfigs } = useTestConfigurations();
    const { data: prompts = [], isLoading: loadingPrompts, refetch: refetchPrompts } = useUserPrompts(
        userId,
        filterConfigId === '' ? undefined : Number(filterConfigId),
    );

    const filteredPrompts = useMemo(() => {
        if (!filterStatus) return prompts;
        return prompts.filter((p) => p.status === filterStatus);
    }, [prompts, filterStatus]);

    const configById = useMemo(() => {
        const m: Record<number, { name: string }> = {};
        testConfigs.forEach((c) => { m[c.id] = { name: c.name }; });
        return m;
    }, [testConfigs]);

    const handleGenerate = async (promptId: number) => {
        const reportId = table?.getSelectedRowModel()?.rows[0]?.original?.id;
        if (reportId == null) {
            alert(t('userPrompts.selectReportBelow'));
            return;
        }
        setIsGenerating(true);
        setRecommendations(null);
        try {
            const result = await authService.generateRecommendationsByReportId({
                userId,
                reportId,
                promptId,
            });
            if (result.data.success) {
                setRecommendations(result.data.recommendations ?? '');
            } else {
                alert(result.data.error ?? t('reportDetail.genError'));
            }
        } catch (e) {
            console.error(e);
            alert(t('users.genFailed'));
        } finally {
            setIsGenerating(false);
        }
    };

    const handleDelete = async (prompt: UserPrompt) => {
        if (prompt.isGlobal) return;
        if (!window.confirm(t('prompts.deleteConfirm'))) return;
        try {
            await promptsService.deleteUserPrompt(prompt.id, userId);
            refetchPrompts();
        } catch (e) {
            console.error(e);
            alert(t('prompts.deleteFailed'));
        }
    };

    const formatDate = (s: string) =>
        new Date(s).toLocaleString(navigator.language, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });

    if (loadingConfigs) return <CircularProgress />;

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Select
                    size="small"
                    displayEmpty
                    value={filterConfigId}
                    onChange={(e) => setFilterConfigId(e.target.value === '' ? '' : Number(e.target.value))}
                    sx={{ minWidth: 200 }}
                >
                    <MenuItem value="">{t('prompts.allReportTypes')}</MenuItem>
                    {testConfigs.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                </Select>
                <Select
                    size="small"
                    displayEmpty
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    sx={{ minWidth: 140 }}
                >
                    <MenuItem value="">{t('prompts.allStatuses')}</MenuItem>
                    <MenuItem value="active">{t('prompts.status.active')}</MenuItem>
                    <MenuItem value="draft">{t('prompts.status.draft')}</MenuItem>
                    <MenuItem value="archived">{t('prompts.status.archived')}</MenuItem>
                </Select>
                <Button variant="contained" onClick={() => setCreateOpen(true)}>
                    {t('prompts.add')}
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('prompts.colReportType')}</TableCell>
                            <TableCell>{t('prompts.colName')}</TableCell>
                            <TableCell>{t('prompts.colStatus')}</TableCell>
                            <TableCell>{t('prompts.colUpdated')}</TableCell>
                            <TableCell align="right">{t('prompts.colActions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingPrompts ? (
                            <TableRow><TableCell colSpan={5} align="center"><CircularProgress /></TableCell></TableRow>
                        ) : filteredPrompts.length === 0 ? (
                            <TableRow><TableCell colSpan={5} align="center">{t('prompts.empty')}</TableCell></TableRow>
                        ) : (
                            filteredPrompts.map((p) => (
                                <TableRow key={p.id}>
                                    <TableCell>{configById[p.testConfigurationId]?.name ?? p.testConfigurationId}</TableCell>
                                    <TableCell>
                                        {p.name}
                                        {p.isGlobal ? ` (${t('prompts.allUsers')})` : ''}
                                    </TableCell>
                                    <TableCell>{statusLabel(p.status)}</TableCell>
                                    <TableCell>{formatDate(p.updatedAt)}</TableCell>
                                    <TableCell align="right">
                                        {!p.isGlobal && (
                                            <Tooltip title={t('prompts.editHint')}>
                                                <IconButton size="small" onClick={() => setEditingPrompt(p)}><EditIcon /></IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title={t('prompts.historyHint')}>
                                            <IconButton size="small" onClick={() => setVersionsPromptId(p.id)}><HistoryIcon /></IconButton>
                                        </Tooltip>
                                        <Tooltip title={isGenerating ? t('users.generating') : t('userPrompts.getAiRecommendations')}>
                                            <IconButton size="small" onClick={() => handleGenerate(p.id)} disabled={isGenerating}>
                                                {isGenerating ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                                            </IconButton>
                                        </Tooltip>
                                        {!p.isGlobal && (
                                            <Tooltip title={t('prompts.deleteHint')}>
                                                <IconButton size="small" color="error" onClick={() => handleDelete(p)}><DeleteIcon /></IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {recommendations && (
                <Box mt={2} p={2} border={1} borderColor="divider" borderRadius={1}>
                    <Typography variant="subtitle1" gutterBottom>{t('keyValue.generatedRecs')}</Typography>
                    <div style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: recommendations.replace(/\n/g, '<br>') }} />
                </Box>
            )}

            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                {t('userPrompts.selectReportHint')}
            </Typography>

            {createOpen && (
                <PromptFormDialog
                    userId={userId}
                    testConfigs={testConfigs}
                    onClose={() => setCreateOpen(false)}
                    onSaved={() => { setCreateOpen(false); refetchPrompts(); }}
                />
            )}
            {editingPrompt && (
                <PromptFormDialog
                    userId={userId}
                    testConfigs={testConfigs}
                    prompt={editingPrompt}
                    onClose={() => setEditingPrompt(null)}
                    onSaved={() => { setEditingPrompt(null); refetchPrompts(); }}
                />
            )}
            {versionsPromptId != null && (
                <VersionsDialog
                    promptId={versionsPromptId}
                    userId={userId}
                    onClose={() => setVersionsPromptId(null)}
                />
            )}
        </Box>
    );
};

interface PromptFormDialogProps {
    userId: number;
    testConfigs: { id: number; name: string }[];
    prompt?: UserPrompt | null;
    onClose: () => void;
    onSaved: () => void;
}

const PromptFormDialog: React.FC<PromptFormDialogProps> = ({ userId, testConfigs, prompt, onClose, onSaved }) => {
    const [name, setName] = useState(prompt?.name ?? '');
    const [testConfigurationId, setTestConfigurationId] = useState<number>(prompt?.testConfigurationId ?? testConfigs[0]?.id ?? 0);
    const [status, setStatus] = useState<'draft' | 'active'>(prompt?.status === 'archived' ? 'draft' : (prompt?.status ?? 'draft'));
    const [value, setValue] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);

    const { data: fullPrompt, isLoading: loadingPrompt } = useUserPrompt(prompt?.id, userId);

    React.useEffect(() => {
        if (!prompt) return;
        setName(prompt.name);
        setTestConfigurationId(prompt.testConfigurationId);
        setStatus(prompt.status === 'archived' ? 'draft' : prompt.status);
    }, [prompt]);

    React.useEffect(() => {
        if (fullPrompt?.versions?.length) {
            const v = fullPrompt.versions.find((x) => x.id === fullPrompt.currentVersionId) ?? fullPrompt.versions[0];
            setValue(v.value);
            setDescription(v.description ?? '');
        }
    }, [fullPrompt]);

    const handleSave = async (asDraft: boolean) => {
        const s = asDraft ? 'draft' : 'active';
        if (!name.trim()) { alert(t('prompts.enterName')); return; }
        if (!value.trim() && !prompt) { alert(t('prompts.enterText')); return; }
        setSaving(true);
        try {
            if (prompt) {
                await promptsService.updateUserPrompt(
                    prompt.id,
                    { name, status: s, value: value.trim() || undefined, description: description || undefined },
                    userId,
                );
            } else {
                const dto: CreateUserPromptDto = {
                    userId,
                    testConfigurationId,
                    name: name.trim(),
                    status: s,
                    value: value.trim() || ' ',
                    description: description || undefined,
                };
                await promptsService.createUserPrompt(dto);
            }
            onSaved();
        } catch (e) {
            console.error(e);
            alert(t('prompts.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="md" onClose={onClose}>
            <DialogTitle>{prompt ? `${t('prompts.editTitlePrefix')} ${prompt.name}` : t('prompts.newTitle')}</DialogTitle>
            <DialogContent>
                {prompt && loadingPrompt ? (
                    <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
                ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField label={t('prompts.name')} value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth />
                    <TextField
                        select
                        label={t('prompts.reportType')}
                        value={testConfigurationId}
                        onChange={(e) => setTestConfigurationId(Number(e.target.value))}
                        size="small"
                        fullWidth
                        disabled={!!prompt}
                    >
                        {testConfigs.map((c) => (
                            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                        ))}
                    </TextField>
                    <Typography variant="body2">{t('userPrompts.howSave')}</Typography>
                    <RadioGroup row value={status} onChange={(e) => setStatus(e.target.value as 'draft' | 'active')}>
                        <FormControlLabel value="active" control={<Radio />} label={t('prompts.publishNow')} />
                        <FormControlLabel value="draft" control={<Radio />} label={t('prompts.saveAsDraft')} />
                    </RadioGroup>
                    <TextField label={t('prompts.promptText')} multiline minRows={10} value={value} onChange={(e) => setValue(e.target.value)} fullWidth />
                    <TextField label={t('userPrompts.noteForSelf')} multiline minRows={2} value={description} onChange={(e) => setDescription(e.target.value)} fullWidth />
                </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button onClick={() => handleSave(true)} disabled={saving}>{t('prompts.saveDraftBtn')}</Button>
                <Button variant="contained" onClick={() => handleSave(false)} disabled={saving}>{t('prompts.publish')}</Button>
            </DialogActions>
        </Dialog>
    );
};

interface VersionsDialogProps {
    promptId: number;
    userId: number;
    onClose: () => void;
}

const SNIPPET_LEN = 120;

const VersionsDialog: React.FC<VersionsDialogProps> = ({ promptId, userId, onClose }) => {
    const { data: prompt } = useUserPrompt(promptId, userId);
    const { data: versions = [] } = useUserPromptVersions(promptId, userId);
    const { data: branches = [] } = useUserPromptBranches(promptId, userId);
    const [creatingBranch, setCreatingBranch] = useState(false);
    const setCurrentVersion = useSetCurrentVersion(promptId, userId);

    const formatDate = (s: string) =>
        new Date(s).toLocaleString(navigator.language, {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });

    const handleCreateBranch = async () => {
        setCreatingBranch(true);
        try {
            await promptsService.createBranch(promptId, undefined, userId);
            onClose();
        } catch (e) {
            console.error(e);
            alert(t('prompts.branchFailed'));
        } finally {
            setCreatingBranch(false);
        }
    };

    const currentVersionId = prompt?.currentVersionId ?? null;

    return (
        <Dialog open fullWidth maxWidth="md" onClose={onClose}>
            <DialogTitle>{t('prompts.versionTitle')}</DialogTitle>
            <DialogContent>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>{t('prompts.savedRevisions')}</Typography>
                <TableContainer><Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('prompts.when')}</TableCell>
                            <TableCell>{t('prompts.version')}</TableCell>
                            <TableCell sx={{ minWidth: 200 }}>{t('prompts.content')}</TableCell>
                            <TableCell align="center" sx={{ width: 100 }}>{t('prompts.activeCol')}</TableCell>
                            <TableCell align="center" sx={{ width: 120 }}>{t('prompts.actionsCol')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {versions.map((v) => {
                            const isActive = v.id === currentVersionId;
                            const snippet = v.value.length > SNIPPET_LEN
                                ? v.value.slice(0, SNIPPET_LEN) + '…'
                                : v.value;
                            return (
                                <TableRow key={v.id}>
                                    <TableCell>{formatDate(v.createdAt)}</TableCell>
                                    <TableCell>#{v.versionNumber}</TableCell>
                                    <TableCell>
                                        <Tooltip title={v.value || ''} placement="top-start">
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 320 }} noWrap>
                                                {snippet || '—'}
                                            </Typography>
                                        </Tooltip>
                                        {v.description && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                {v.description}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {isActive ? <CheckCircleIcon color="primary" fontSize="small" /> : '—'}
                                    </TableCell>
                                    <TableCell align="center">
                                        {!isActive && !prompt?.isGlobal && (
                                            <Tooltip title={t('prompts.makeActive')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setCurrentVersion.mutate(v.id)}
                                                    disabled={setCurrentVersion.isPending}
                                                >
                                                    <RestoreIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table></TableContainer>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>{t('prompts.testCopies')}</Typography>
                {branches.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">{t('prompts.noCopies')}</Typography>
                ) : (
                    <Box component="ul" sx={{ pl: 2 }}>
                        {branches.map((b) => (
                            <li key={b.id}>{b.branchName ?? b.name} — {formatDate(b.createdAt)}</li>
                        ))}
                    </Box>
                )}
                {!prompt?.isGlobal && (
                    <Button size="small" onClick={handleCreateBranch} disabled={creatingBranch} sx={{ mt: 1 }}>
                        {creatingBranch ? t('prompts.creating') : t('prompts.createCopy')}
                    </Button>
                )}
            </DialogContent>
            <DialogActions><Button onClick={onClose}>{t('common.close')}</Button></DialogActions>
        </Dialog>
    );
};

export default UserPromptsManager;
